import type { Text } from '../Text';
import type { TextExtension, TextMatcher } from '../types';

/**
 * Extension interface providing pattern matching methods.
 */
export interface MatchingExtension extends Text {
  /**
   * Checks if the string matches the provided matcher.
   * Supports boolean, string (exact match), RegExp, or predicate function.
   */
  matches(matcher: TextMatcher): boolean;

  /**
   * Checks if the string matches ALL of the provided matchers (AND logic).
   */
  matchesAll(matchers: Iterable<TextMatcher>): boolean;

  /**
   * Checks if the string matches ANY of the provided matchers (OR logic).
   */
  matchesAny(matchers: Iterable<TextMatcher>): boolean;
}

/**
 * Creates an extension that adds pattern matching methods to Text.
 *
 * @example
 * const t = $$$('hello', withMatching());
 * t.matches(/^h/);           // true
 * t.matches('hello');        // true (exact match)
 * t.matches(s => s.length > 3); // true
 *
 * @example
 * const t = $$$('hello', withMatching());
 * t.matchesAll([/^h/, /o$/]); // true (AND - all must pass)
 * t.matchesAny([/^x/, /o$/]); // true (OR - any must pass)
 */
export function withMatching(): TextExtension<MatchingExtension> {
  return (text) => {
    const $$ = text.$$;

    return {
      matches(matcher: TextMatcher): boolean {
        return testMatcher($$, matcher);
      },

      matchesAll(matchers: Iterable<TextMatcher>): boolean {
        for (const matcher of matchers) {
          if (!testMatcher($$, matcher)) return false;
        }
        return true;
      },

      matchesAny(matchers: Iterable<TextMatcher>): boolean {
        for (const matcher of matchers) {
          if (testMatcher($$, matcher)) return true;
        }
        return false;
      },
    };
  };
}

function testMatcher(str: string, matcher: TextMatcher): boolean {
  switch (typeof matcher) {
    case 'boolean':
      return matcher;
    case 'string':
      return str === matcher;
    case 'object':
      if (matcher instanceof RegExp) {
        const flags = matcher.flags.replace(/[gy]/g, '');
        const safeMatcher = new RegExp(matcher.source, flags);
        return safeMatcher.test(str);
      }
      return false;
    case 'function':
      return matcher(str);
  }
  return false;
}
